import React, { useState } from 'react';
import { Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import getWizardStep from 'utils/getWizardStep';
import { POST } from 'utils/httpMethods';
import Spinner from 'components/Spinner';
import FormButtons from '../../../FormButtons';
import Form from '../../../simulation/FormStep';
import getWizard from '../../per/wizardData';
import getWizardLifeInsurance from '../../lifeInsurance/wizardData';
import getWizardManagementAct from '../../managementAct/wizardData';
import { getHubConfig, getErrorMessage } from './hubConfig';
import withRouter from '../../../withRouter';
import './SubscriptionHubWarning.scss';

/**
 * Composant générique pour l'avertissement de redirection vers un portail de souscription
 * Utilisé pour PER (MMA, Cardif) et Assurance Vie (MMA, Primonial)
 *
 * @param {string} hubConfigKey - Clé de configuration du portail (ex: 'PER_MMA', 'LIFE_INSURANCE_PRIMONIAL')
 * @param {object} location - Objet location du router
 * @param {object} params - Paramètres de l'URL (id, contractId, contractType)
 * @param {function} navigate - Fonction de navigation
 */
const SubscriptionHubWarning = ({ hubConfigKey, location, params, navigate }) => {
    const { id, contractId, managementActId, contractType, compagnyChoice } = params;
    const [loading, setLoading] = useState(false);
    const [error, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);

    // Récupération de la configuration du portail
    const hubConfig = getHubConfig(hubConfigKey);

    // Configuration des URLs et du wizard
    const listUrl = `/rip/${id}/contracts/`;

    // Sélection du bon wizard selon le type
    let getWizardFn;
    let wizard;

    switch (hubConfig.type) {
        case 'per':
            getWizardFn = getWizard;
            wizard = getWizardFn(contractType);
            break;
        case 'lifeInsurance':
            getWizardFn = getWizardLifeInsurance;
            wizard = getWizardFn(contractType);
            break;
        case 'managementAct':
            getWizardFn = getWizardManagementAct;
            wizard = getWizardFn(compagnyChoice);
            break;
        default:
            throw new Error(`Type de hub inconnu : ${hubConfig.type}`);
    }

    const baseUrl = wizard.prefixTemplate(params);
    const previousSlug = getWizardStep({ wizard, url: location.pathname, next: false });
    const previous = previousSlug ? `${baseUrl}/${previousSlug}` : `/rip/${id}/contracts/new`;

    /**
     * Gère la redirection vers le portail de souscription
     */
    const redirectToHub = async (event) => {
        event.preventDefault();
        setLoading(true);
        setIsError(false);

        try {
            // Pour les actes de gestion, on utilise managementActId, sinon contractId
            const entityId = managementActId || contractId;

            // Appel à l'API avec l'endpoint spécifique au portail
            const response = await POST(hubConfig.apiEndpoint(entityId));
            const { url } = await response.json();

            // Redirection avec l'URL transformée si nécessaire (ex: SSO Primonial)
            const redirectUrl = hubConfig.redirectUrl(url);

            // Ouverture du portail dans un nouvel onglet
            window.open(redirectUrl, '_blank');

            // Redirection vers la liste des contrats
            navigate(listUrl);
        } catch (e) {
            setIsError(true);

            // Tentative de récupération d'un message d'erreur personnalisé
            let customMessage = null;
            try {
                if (e.response) {
                    const errorResponse = await e.response.json();
                    customMessage = errorResponse.message;
                }
            } catch (parseError) {
                // Si le parsing échoue, on utilise les messages par défaut
            }

            // Définition du message d'erreur selon le code HTTP
            const message = getErrorMessage(e.status, customMessage);
            setErrorMessage(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form
            handleSubmit={redirectToHub}
            wizard={wizard}
            noScroll
        >
            <div className="alert-container">
                <Alert bsStyle="info">
                    <strong>Attention</strong>
                    <br />
                    A partir de cette étape la souscription sera initialisée sur le site partenaire et toute
                    modification ultérieure du RIP n&apos;entrainera pas la mise à jour automatique sur le site du partenaire.
                    Ces modifications devront obligatoirement se faire en manuel sur ce dernier.
                </Alert>
            </div>
            {loading && <Spinner>{hubConfig.spinnerText}</Spinner>}
            {error && !loading && (
                <div className="alert-container">
                    <Alert bsStyle="danger">
                        <strong>Erreur</strong>
                        <br />
                        {errorMessage}
                    </Alert>
                </div>
            )}
            <FormButtons
                list={listUrl}
                back={previous}
            />
        </Form>
    );
};

SubscriptionHubWarning.propTypes = {
    hubConfigKey: PropTypes.string.isRequired,
    params: PropTypes.shape({
        id: PropTypes.string,
        contractId: PropTypes.string,
        managementActId: PropTypes.string,
        contractType: PropTypes.string,
        compagnyChoice: PropTypes.string,
    }).isRequired,
    navigate: PropTypes.func,
    location: PropTypes.shape({
        pathname: PropTypes.string,
    }).isRequired,
};

export default withRouter(SubscriptionHubWarning);
